import { inject, Injectable } from '@angular/core';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { IRecommendationTrackSource } from '@epicuro-next/models/recommendation-panels';
import {
  getListIdFromProductListUrl,
  isListUrl,
  isLookbookUrl,
  isSearchUrl,
} from '@epicuro-next/navigation/routes';
import { setProductSource } from '@epicuro-next/state/config-strip-state';
import { trackList } from '@epicuro-next/state/product-list-state';
import { recommendationUrlRedirect } from '@epicuro-next/state/recommendation-panels-state';
import { go } from '@epicuro-next/state/router-store';
import { stringifyObjectValues, urlToLinkInfo } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, switchMap } from 'rxjs';

@Injectable()
export class PanelRedirectEffects {
  private readonly actions$ = inject(Actions);

  public searchRedirectLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(recommendationUrlRedirect),
      filter(({ routerLink }) => !!routerLink && isSearchUrl(routerLink)),
      switchMap((payload) => {
        const { routerLink, seeMore } = payload;
        const { url, params } = urlToLinkInfo(routerLink);

        const recommendationPanel = payloadToSourceRecommendationPanel(payload);

        if (seeMore) {
          recommendationPanel.seeMore = seeMore;
        }

        return [
          setProductSource({
            source: BasketItemSource.RECOMMENDATION_PANEL,
            details: {
              recommendationPanel: {
                ...recommendationPanel,
                correlationId: recommendationPanel.correlationId ?? routerLink,
              } as IRecommendationTrackSource,
            },
            additionalUiDetails: {
              query: params?.q ?? '',
              // needs to stringify values before future comparison
              searchParams: params ? stringifyObjectValues(params) : null,
            },
          }),
          go({
            commands: [url],
            extras: {
              queryParams: params,
            },
          }),
        ];
      }),
    ),
  );

  public productListRedirectLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(recommendationUrlRedirect),
      filter(({ routerLink }) => !!routerLink && isListUrl(routerLink)),
      switchMap((payload) => {
        const { panelName, panelStyle, servingConfig, routerLink } = payload;
        const { url, params } = urlToLinkInfo(routerLink);
        const listId = getListIdFromProductListUrl(url);

        return [
          trackList({
            payload: {
              source: 'recommendation-panels',
              productListId: listId,
            },
          }),
          setProductSource({
            source: BasketItemSource.RECOMMENDATION_PANEL,
            details: {
              recommendationPanel: {
                panelName,
                panelStyle,
                servingConfig,
                correlationId: listId,
              } as IRecommendationTrackSource,
              listId: [listId],
            },
            additionalUiDetails: {
              listId: [listId],
            },
          }),
          go({
            commands: [url],
            extras: {
              queryParams: params,
            },
          }),
        ];
      }),
    ),
  );

  public lookbookRedirectLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(recommendationUrlRedirect),
      filter(({ routerLink }) => !!routerLink && isLookbookUrl(routerLink)),
      switchMap((payload) => {
        const { panelName, panelStyle, servingConfig, routerLink } = payload;
        const { url, params } = urlToLinkInfo(routerLink);

        const [listId = '', lookbookId = ''] = url.split('/').reverse();
        return [
          trackList({
            payload: {
              source: 'recommendation-panels',
              productListId: listId,
            },
          }),
          setProductSource({
            source: BasketItemSource.RECOMMENDATION_PANEL,
            details: {
              lookBookId: [lookbookId],
              recommendationPanel: {
                panelName,
                panelStyle,
                servingConfig,
                correlationId: listId,
              } as IRecommendationTrackSource,
            },
            additionalUiDetails: {
              listId: [listId],
            },
          }),
          go({
            commands: [url],
            extras: {
              queryParams: params,
            },
          }),
        ];
      }),
    ),
  );
}

function payloadToSourceRecommendationPanel<
  T extends Partial<IRecommendationTrackSource>,
>(payload: T): Partial<IRecommendationTrackSource> {
  const {
    attributionToken,
    correlationId,
    hash,
    panelName,
    panelStyle,
    servingConfig,
    providerId,
  } = payload;
  return {
    attributionToken,
    correlationId,
    hash,
    panelName,
    panelStyle,
    servingConfig,
    providerId,
  };
}
